// src/pages/public/Signup.jsx
// Diseño: réplica exacta de screens_auth.jsx del handoff OTROS (13).zip
// Layout: 2 columnas desktop (hero oscuro izq + form der), 1 columna mobile.
// Lógica auth INTACTA: signupWithEmail, loginWithGoogle, useEffect redirect,
// destinationFor, navType POP, showSplash, validación password, checkbox accept.
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useNavigationType, useSearchParams } from 'react-router-dom';
import { signupWithEmail, loginWithGoogle, authErrorMessage } from '../../auth/authHelpers';
import { useAuth } from '../../auth/AuthContext';
import { auth } from '../../firebase';
import { Field, Divider, ErrorBanner } from './_authShared';
import EELogo from '../../components/EELogo';
import SplashScreen from '../../components/SplashScreen';

function destinationFor(userData) {
  if (!userData) return '/dashboard';
  if (userData.role === 'admin') return '/admin/dashboard';
  return '/dashboard';
}

// Promo beta gratuita (/promo/beta → /signup?promo=beta). El canje se hace al
// completar el signup (splash). Endpoint HTTP, ver freeTrialClaim.js.
const PROMO_BETA_KEY = 'ee_promo_beta';
const CLAIM_URL =
  'https://southamerica-east1-mati-edge-elite.cloudfunctions.net/claimFreeTrialToken';

async function claimBetaIfPromo() {
  if (localStorage.getItem(PROMO_BETA_KEY) !== '1') return;
  try {
    const current = auth.currentUser;
    if (!current) return;
    const idToken = await current.getIdToken();
    await fetch(CLAIM_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
      body: JSON.stringify({}),
    });
  } catch (_e) {
    // best-effort: si falla, el usuario igual entra; puede reintentar en /promo/beta
  } finally {
    localStorage.removeItem(PROMO_BETA_KEY);
  }
}

export default function Signup() {
  const navigate = useNavigate();
  const navType = useNavigationType();
  const [searchParams] = useSearchParams();
  const { user, userData, loading: authLoading } = useAuth();
  const [accept, setAccept] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [nombre, setNombre] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSplash, setShowSplash] = useState(false);

  // Al entrar a /signup capturamos (una sola vez) si el usuario YA llegó con
  // sesión activa. Mismo patrón que Login (detecta sesión activa on-mount vía
  // useAuth + guard POP + guard race de userData), pero como /signup es el
  // entrypoint "crear cuenta + pagar", una sesión ya logueada salta ese paso
  // y va directo a /checkout. Un signup FRESCO (el user aparece DESPUÉS del
  // mount) conserva su flujo: splash→dashboard o /verificar-email.
  const arrivedLoggedIn = useRef(null);

  useEffect(() => {
    if (authLoading) return;                        // esperar auth: sin flash ni redirect prematuro
    if (user && !userData) return;                  // esperar datos (fix race, igual que Login)
    if (navType === 'POP') return;                  // respetar back button → no auto-redirigir
    if (arrivedLoggedIn.current === null) arrivedLoggedIn.current = !!user;
    if (user) {
      if (arrivedLoggedIn.current) {
        navigate('/checkout', { replace: true });   // sesión activa al entrar → directo a pagar
      } else if (!user.emailVerified) {
        navigate('/verificar-email', { replace: true });
      } else if (!showSplash) {
        setShowSplash(true);
      }
    }
  }, [user, userData, authLoading, navigate, navType, showSplash]);

  // Persistir el flag promo=beta apenas se monta (sobrevive el redirect a
  // /verificar-email y el flujo de verificación por email).
  useEffect(() => {
    if (searchParams.get('promo') === 'beta') {
      localStorage.setItem(PROMO_BETA_KEY, '1');
    }
  }, [searchParams]);

  const handleSplashComplete = async () => {
    await claimBetaIfPromo();
    navigate(destinationFor(userData), { replace: true });
  };

  const handleSubmit = async () => {
    if (!accept || loading) return;
    setError('');
    if (password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    setLoading(true);
    try {
      await signupWithEmail(email.trim(), password, nombre.trim());
    } catch (err) {
      setError(authErrorMessage(err));
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    if (loading) return;
    setError('');
    setLoading(true);
    try {
      await loginWithGoogle();
      setShowSplash(true);
    } catch (err) {
      setError(authErrorMessage(err));
      setLoading(false);
    }
  };

  if (showSplash) {
    return <SplashScreen type="login" onComplete={handleSplashComplete} />;
  }

  return (
    <div className="mobile-root">
      <section
        className="screen signup-screen"
        style={{ padding: 0, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}
      >

        {/* Panel izquierdo — solo desktop */}
        <div className="signup-desktop-panel">
          <EELogo size={28} color="#fff" />
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 32, fontWeight: 800, letterSpacing: '-0.03em', margin: '28px 0 12px', lineHeight: 1.1, color: '#fff' }}>
            Análisis deportivo en tiempo real. ⚡
          </h2>
          <p style={{ fontSize: 15, color: 'rgba(255,255,255,.65)', margin: '0 0 32px', lineHeight: 1.55 }}>
            Señales en vivo cuando el modelo detecta valor. Sin ruido, sin análisis manual.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {[
              { icon: '📲', t: 'Alertas por Telegram', d: 'Llegan al instante con cuota y nivel de confianza.' },
              { icon: '📊', t: 'Dashboard con picks del día', d: 'NBA, Euroliga y +25 ligas analizadas en vivo.' },
              { icon: '📈', t: 'Historial y ROI real', d: 'Filtra por liga, fecha y tipo. Sin datos inventados.' },
            ].map((b, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(255,255,255,.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 17, flexShrink: 0 }}>{b.icon}</div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: '#fff', marginBottom: 2 }}>{b.t}</div>
                  <div style={{ fontSize: 12, color: 'rgba(255,255,255,.5)', lineHeight: 1.4 }}>{b.d}</div>
                </div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 36, padding: '12px 16px', background: 'rgba(255,255,255,.06)', borderRadius: 12, display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ display: 'flex' }}>
              {[['J', '#4285F4'], ['C', '#DB4437'], ['M', '#0F9D58'], ['A', '#F4B400'], ['S', '#AB47BC']].map(([l, c], i) => (
                <div key={l} style={{ width: 28, height: 28, borderRadius: 999, background: c, border: '2px solid #1a1410', marginLeft: i ? -10 : 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, color: '#fff', fontFamily: 'var(--font-display)' }}>
                  {l}
                </div>
              ))}
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', lineHeight: 1.3 }}>Javier Muñoz, Camila Rojas</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,.5)', marginTop: 2 }}>y 18 más</div>
            </div>
          </div>
        </div>

        {/* Lado formulario */}
        <div className="signup-form-side">

          {/* Back */}
          <div style={{ padding: '20px 20px 0' }}>
            <button
              onClick={() => navigate('/')}
              style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'transparent', border: 0, color: 'var(--muted)', fontSize: 14, fontWeight: 500, cursor: 'pointer', padding: 0 }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 6l-6 6 6 6" />
              </svg>
              Volver
            </button>
          </div>

          {/* Contenido — centrado verticalmente */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: '0 24px' }}>

            <div className="signup-logo-mobile" style={{ marginBottom: 10, display: 'inline-flex' }}>
              <EELogo size={22} />
            </div>

            <div style={{ marginBottom: 36 }}>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 28, fontWeight: 700, letterSpacing: '-0.025em', margin: '12px 0 8px', lineHeight: 1.15 }}>
                Comienza hoy ⚡
              </h2>
              <p style={{ fontSize: 14, color: 'var(--muted)', margin: 0, lineHeight: 1.45 }}>
                Accede al análisis en tiempo real · Sin compromiso
              </p>
            </div>

            <ErrorBanner msg={error} />

            {/* Google — acción principal */}
            <button
              className="btn-google"
              style={{ fontWeight: 600, fontSize: 16, padding: '18px 24px', boxShadow: '0 4px 24px rgba(0,0,0,.13)', border: '1.5px solid #E0E0E5', borderRadius: 16, marginBottom: 10 }}
              onClick={handleGoogle}
              disabled={loading}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                <path fill="#4285F4" d="M22.5 12.3c0-.8-.1-1.5-.2-2.3H12v4.4h5.9c-.3 1.4-1 2.6-2.2 3.3v2.7h3.5c2.1-1.9 3.3-4.7 3.3-8.1z" />
                <path fill="#34A853" d="M12 23c3 0 5.5-1 7.3-2.6l-3.5-2.7c-1 .7-2.2 1.1-3.8 1.1-2.9 0-5.3-2-6.2-4.6H2.2v2.9C4 20.6 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.8 14.2c-.2-.7-.4-1.4-.4-2.2s.1-1.5.4-2.2V6.9H2.2C1.4 8.4 1 10.1 1 12s.4 3.6 1.2 5.1l3.6-2.9z" />
                <path fill="#EA4335" d="M12 5.4c1.6 0 3.1.6 4.2 1.6l3.1-3.1C17.5 2.1 15 1 12 1 7.7 1 4 3.4 2.2 6.9l3.6 2.9C6.7 7.4 9.1 5.4 12 5.4z" />
              </svg>
              Continuar con Google
            </button>

            {/* Términos implícitos (Google) */}
            <p style={{ textAlign: 'center', fontSize: 11, color: 'var(--muted)', margin: '0 0 14px', lineHeight: 1.5 }}>
              Al continuar aceptas los{' '}
              <a onClick={(e) => { e.stopPropagation(); navigate('/terminos'); }} style={{ color: 'var(--muted)', textDecoration: 'underline', cursor: 'pointer' }}>Términos de uso</a>
              {' '}y la{' '}
              <a onClick={(e) => { e.stopPropagation(); navigate('/privacidad'); }} style={{ color: 'var(--muted)', textDecoration: 'underline', cursor: 'pointer' }}>Política de privacidad</a>
            </p>

            {/* Email — colapsado por defecto */}
            {!showEmail ? (
              <div style={{ textAlign: 'center' }}>
                <button
                  onClick={() => setShowEmail(true)}
                  style={{ background: 'transparent', border: 0, color: 'var(--muted)', fontSize: 13, fontWeight: 500, cursor: 'pointer', padding: '8px 0' }}
                >
                  Usar email y contraseña →
                </button>
              </div>
            ) : (
              <div>
                <Divider text="o" />
                <div className="input-group">
                  <Field label="Nombre" placeholder="Tu nombre" value={nombre} onChange={setNombre} autoComplete="name" />
                  <Field label="Email" type="email" placeholder="tu@email.com" value={email} onChange={setEmail} autoComplete="email" />
                  <Field
                    label="Contraseña"
                    type="password"
                    placeholder="Mínimo 8 caracteres"
                    hint="Usa al menos 8 caracteres con números y letras"
                    value={password}
                    onChange={setPassword}
                    autoComplete="new-password"
                    right={
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--muted)" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    }
                  />
                </div>

                <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginTop: 18, cursor: 'pointer' }}>
                  <div
                    onClick={() => setAccept(!accept)}
                    style={{ width: 20, height: 20, borderRadius: 5, border: `2px solid ${accept ? 'var(--ink)' : 'var(--line-strong)'}`, background: accept ? 'var(--ink)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none', marginTop: 1 }}
                  >
                    {accept && (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5">
                        <path d="M5 12l5 5 9-11" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.45 }}>
                    Acepto los{' '}
                    <a onClick={(e) => { e.stopPropagation(); navigate('/terminos'); }} style={{ color: 'var(--ink)', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}>Términos</a>
                    {' '}y la{' '}
                    <a onClick={(e) => { e.stopPropagation(); navigate('/privacidad'); }} style={{ color: 'var(--ink)', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}>Política de privacidad</a>
                    . Confirmo ser mayor de 18 años.
                  </div>
                </label>

                <button
                  className="btn-primary accent"
                  style={{ marginTop: 18, opacity: accept && !loading ? 1 : 0.5, pointerEvents: accept && !loading ? 'auto' : 'none' }}
                  onClick={handleSubmit}
                >
                  {loading ? 'Creando...' : 'Crear cuenta'}
                </button>
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={{ padding: '20px 24px 40px', textAlign: 'center', fontSize: 13, color: 'var(--muted)' }}>
            ¿Ya tienes cuenta?{' '}
            <a onClick={() => navigate('/login')} style={{ color: 'var(--ink)', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}>
              Inicia sesión
            </a>
          </div>

        </div>
      </section>
    </div>
  );
}
